/* eslint-disable import/no-extraneous-dependencies */
import { AuthenticationApi } from '@coretava/api/security-service';
import { getRefreshTokenLocalStorageKey, getTokenLocalStorageKey } from '@gamiphy/loyalty-station-desktop/store/user/constants';
import { getLocalStorageItem, setLocalStorageItem } from '@gamiphy/utils/storage';
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export interface TokenRefreshConfig {
  /**
   * Function to get the current app ID
   */
  getAppId: () => string | undefined;
  /**
   * Whether to enable credentials (cookies) with requests
   * @default true
   */
  enableCredentials?: boolean;
  /**
   * Callback when token refresh succeeds
   */
  onRefreshSuccess?: (accessToken: string, refreshToken: string) => void;
  /**
   * Callback when token refresh fails (e.g., redirect to login)
   */
  onRefreshFailed?: (error: AxiosError) => void;
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

/**
 * Setup axios interceptor for automatic token refresh on 401 errors
 * @param axiosInstance - The axios instance to configure
 * @param config - Configuration for token refresh
 * @returns The configured axios instance
 */
export function setupTokenRefreshInterceptor(
  axiosInstance: AxiosInstance,
  config: TokenRefreshConfig,
): AxiosInstance {
  const {
    getAppId, enableCredentials = true, onRefreshSuccess, onRefreshFailed,
  } = config;

  // Request interceptor to ensure credentials are sent with every request
  if (enableCredentials) {
    axiosInstance.interceptors.request.use(
      (requestConfig: InternalAxiosRequestConfig): InternalAxiosRequestConfig => ({ ...requestConfig, withCredentials: true }),
      (error) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
    );
  }

  // Response interceptor to handle 401 errors and refresh token
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { retryAttempted?: boolean };

      if (error.response?.status === 401 && !originalRequest.retryAttempted) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => axiosInstance(originalRequest))
            .catch((err) => Promise.reject(err instanceof Error ? err : new Error(String(err))));
        }

        originalRequest.retryAttempted = true;
        isRefreshing = true;

        try {
          const appId = getAppId() || (originalRequest.headers?.['gamix-app-id'] as string);

          if (!appId) {
            processQueue(error);
            isRefreshing = false;
            return Promise.reject(error);
          }

          const authApi = new AuthenticationApi(
            {
              isJsonMime: () => true,
              basePath: process.env.BASE_API_URL || '',
            },
            undefined,
            axios,
          );

          const response = await authApi.authenticationControllerRefreshToken({
            gamixAppId: appId,
            refreshRequest: {
              refresh_token: getLocalStorageItem(getRefreshTokenLocalStorageKey(appId)) ?? undefined,
            },
          }) as unknown as {
            access_token: string;
            refresh_token: string;
          };
          setLocalStorageItem(getTokenLocalStorageKey(appId), response.access_token);
          setLocalStorageItem(getRefreshTokenLocalStorageKey(appId), response.refresh_token);

          if (onRefreshSuccess) {
            onRefreshSuccess(response.access_token, response.refresh_token);
          }

          processQueue();
          isRefreshing = false;

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          const axiosRefreshError = refreshError as AxiosError;
          processQueue(axiosRefreshError);
          isRefreshing = false;

          if (onRefreshFailed) {
            onRefreshFailed(axiosRefreshError);
          }

          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('token-refresh-failed'));
          }

          return Promise.reject(
            refreshError instanceof Error ? refreshError : new Error(String(refreshError)),
          );
        }
      }

      return Promise.reject(error);
    },
  );

  return axiosInstance;
}

/**
 * Create a configured axios instance with token refresh interceptor
 * @param basePath - The base path for the API
 * @param config - Configuration for token refresh
 * @returns A configured axios instance
 */
export function createAxiosWithTokenRefresh(
  config: TokenRefreshConfig,
): AxiosInstance {
  return setupTokenRefreshInterceptor(axios, config);
}
